import { PagePermissions } from '../constants/pages';

export interface User {
    id: number;
    name: string;
    email: string,
    phone: string,
    role: string;
    warehouse_id?: number;
    company_id?: number;
}

export interface AuthResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
    user: User;
}

export enum OrderDirection {
    INWARD = 'INWARD',
    OUTWARD = 'OUTWARD',
    RETURN = 'RETURN'
}

export enum OrderStatus {
    CREATED = 'CREATED',
    PENDING = 'PENDING',
    PARTIAL_FULFILLED = 'PARTIAL_FULFILLED',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED'
}

export enum ShipmentStatus {
    CREATED = 'CREATED',
    PENDING = 'PENDING',
    PACKED = 'PACKED'
}

export interface OrderItem {
    item_id: number;
    name?: string;
    units_per_carton?: number;
    requested_quantity: number;
    fulfilled_quantity: number;
}

export interface Order {
    id: number;
    partner_id?: number;
    partner_name: string;
    direction: OrderDirection;
    status: OrderStatus;
    item_count?: number;
    items?: OrderItem[];
    created_at: string;
    updated_at?: string;
    fulfilled_at?: string;
    remark?: string;
}

export interface OrderListResponse {
    orders?: Order[];
    data?: Order[];
    meta?: {
        page: number;
        limit: number;
        total?: number;
        totalPages?: number;
    };
    total?: number;
    totalPages?: number;
}

export interface ItemMetadata {
    [key: string]: any;
}

export interface Item {
    id: number;
    name: string;
    category_id: number;
    category_name: string;
    units_per_carton: number;
    min_stock: number;
    metadata?: ItemMetadata;
    remark?: string;
    description?: string;
    is_active: boolean;
    is_enabled: boolean;
    created_at: string;
    updated_at?: string;
}

export interface ItemListResponse {
    data: Item[];
    meta: {
        page: number;
        limit: number;
        total: number;
    };
}

// Category types
export interface Category {
    id: number;
    name: string;
    created_at?: string;
    updated_at?: string;
}

export interface CreateCategoryRequest {
    name: string;
}

export interface UpdateCategoryRequest {
    name?: string;
}

export interface CategoryListResponse {
    categories: Category[];
}

export interface InventoryMovement {
    id: number;
    order_id: number;
    item_id: number;
    direction: 'IN' | 'OUT';
    qty: number;
    created_at: string;
    item?: Item;
}

export interface InventoryItem {
    item_id: number;
    name: string;
    category?: string;
    min_stock?: number;
    available_quantity: number;
}

export type PartnerType = 'CUSTOMER' | 'SUPPLIER';

export interface PartnerMetadata {
    preferred_transports?: string[];
    [key: string]: any;
}

export interface Partner {
    id: number;
    type: PartnerType;
    name: string;
    city?: string;
    district?: string;
    state?: string;
    rating?: number;
    order_cycle_days?: number;
    is_enabled?: boolean;
    metadata?: PartnerMetadata;
    is_active?: boolean;
    created_at?: string;
    updated_at?: string;
}

// Alias for backward compatibility
export type Supplier = Partner;
export type Customer = Partner;

// User Management Types
// Re-export for convenience - use PagePermissions from constants/pages.ts as the source of truth
export type UserPermissions = PagePermissions;

export interface WorkingHours {
    start_time: string;
    end_time: string;
}

export type UserRole = 'WAREHOUSE_TEAM' | 'SALES_TEAM';

export interface ManagedUser {
    id: number;
    email: string;
    name: string;
    role: UserRole;
    permissions: UserPermissions;
    allowed_ips?: string[];
    state_ids?: number[];
    working_hours?: WorkingHours;
    is_enabled: boolean;
    is_active: boolean;
    created_at: string;
}

export interface CreateUserRequest {
    email: string;
    password: string;
    name: string;
    role: UserRole;
    permissions: UserPermissions;
    allowed_ips?: string[];
    state_ids?: number[];
    working_hours?: WorkingHours;
    is_enabled?: boolean;
}

export interface UpdateUserRequest {
    name?: string;
    role?: UserRole;
    permissions?: UserPermissions;
    allowed_ips?: string[];
    state_ids?: number[];
    working_hours?: WorkingHours;
    is_enabled?: boolean;
    is_active?: boolean;
}
